/**
 * Throttle-wrapped markdown renderer for streaming content.
 * Throttles updates to ~12.5 fps, buffers to complete lines, and forces
 * a fresh react-markdown mount on each tick to prevent DOM corruption.
 */

import { memo, useRef } from 'react'

import { useThrottledValue } from '@/hooks/use-throttled-value.hook'

import { ChatMarkdownContent, type ChatMarkdownVariant } from './chat-markdown-content.atom'
import { trimToLastNewline } from './streaming-markdown.helper'

const STREAMING_THROTTLE_MS = 80

export interface StreamingMarkdownContentProps {
  content: string

  variant?: ChatMarkdownVariant

  isStreaming?: boolean

  className?: string
}

const StreamingMarkdownContentBase: React.FC<StreamingMarkdownContentProps> = ({
  content,
  variant = 'assistant',
  isStreaming = false,
  className,
}) => {
  const throttledContent = useThrottledValue(content, STREAMING_THROTTLE_MS, isStreaming)
  const displayContent = isStreaming ? trimToLastNewline(throttledContent) : throttledContent

  const renderCountRef = useRef(0)
  const prevContentRef = useRef(displayContent)

  if (isStreaming && displayContent !== prevContentRef.current) {
    renderCountRef.current += 1
  }
  prevContentRef.current = displayContent

  const markdownKey = isStreaming ? renderCountRef.current : 'complete'

  return (
    <ChatMarkdownContent
      key={markdownKey}
      content={displayContent}
      variant={variant}
      className={className}
    />
  )
}

export const StreamingMarkdownContent = memo(StreamingMarkdownContentBase)

StreamingMarkdownContent.displayName = 'StreamingMarkdownContent'
